import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Globe, Layers, Tag, Info } from 'lucide-react';
import { CategoriesTab, TicketsTab } from './TicketTabs';

const TicketConfigStep = ({ formData, updateFormData }) => {
    const [activeTab, setActiveTab] = useState('regions');
    const [expandedTicketId, setExpandedTicketId] = useState(null);

    // Initialize if not exists
    useEffect(() => {
        if (!formData.ticketRegions) {
            updateFormData({
                ticketRegions: [
                    { id: 'region-1', name: 'INDIAN', currency: 'INR', order: 0 },
                    { id: 'region-2', name: 'INTERNATIONAL', currency: 'USD', order: 1 },
                ],
                ticketCategories: [],
                tickets: [],
                enablePriceByDate: false,
            });
        }
    }, []);

    const addRegion = () => {
        const newRegion = {
            id: `region-${Date.now()}`,
            name: `REGION ${(formData.ticketRegions?.length || 0) + 1}`,
            currency: 'INR', // Default currency
            order: formData.ticketRegions?.length || 0,
        };
        updateFormData({
            ticketRegions: [...(formData.ticketRegions || []), newRegion],
        });
    };

    const updateRegion = (id, updates) => {
        updateFormData({
            ticketRegions: formData.ticketRegions?.map(r =>
                r.id === id ? { ...r, ...updates } : r
            ),
        });
    };

    const deleteRegion = (id) => {
        updateFormData({
            ticketRegions: formData.ticketRegions?.filter(r => r.id !== id),
            tickets: formData.tickets?.filter(t => t.regionType !== id),
        });
    };

    const addCategory = () => {
        const newCategory = {
            id: `category-${Date.now()}`,
            name: `Category ${(formData.ticketCategories?.length || 0) + 1}`,
            order: formData.ticketCategories?.length || 0,
        };
        updateFormData({
            ticketCategories: [...(formData.ticketCategories || []), newCategory],
        });
    };

    const updateCategory = (id, updates) => {
        updateFormData({
            ticketCategories: formData.ticketCategories?.map(c =>
                c.id === id ? { ...c, ...updates } : c
            ),
        });
    };

    const deleteCategory = (id) => {
        updateFormData({
            ticketCategories: formData.ticketCategories?.filter(c => c.id !== id),
            tickets: formData.tickets?.map(t =>
                t.categoryId === id ? { ...t, categoryId: undefined } : t
            ),
        });
    };

    const addTicket = () => {
        const newTicket = {
            id: `ticket-${Date.now()}`,
            name: '',
            price: '', // Changed from 0 to empty string for placeholder support
            regionType: formData.ticketRegions?.[0]?.id || '',
            isActive: true,
            ticketType: 'normal',
            order: 0 // Start at top
        };

        // existing tickets order + 1
        const updatedTickets = (formData.tickets || []).map(t => ({
            ...t,
            order: (t.order || 0) + 1
        }));

        updateFormData({
            tickets: [newTicket, ...updatedTickets],
        });
        setExpandedTicketId(newTicket.id); // Auto-expand new ticket
    };

    const moveTicket = (ticketId, direction) => {
        const tickets = [...(formData.tickets || [])];
        const index = tickets.findIndex(t => t.id === ticketId);
        if (index === -1) return;

        if (direction === 'up' && index > 0) {
            [tickets[index], tickets[index - 1]] = [tickets[index - 1], tickets[index]];
        } else if (direction === 'down' && index < tickets.length - 1) {
            [tickets[index], tickets[index + 1]] = [tickets[index + 1], tickets[index]];
        }

        // Update order fields
        const reorderedTickets = tickets.map((t, idx) => ({ ...t, order: idx }));

        updateFormData({ tickets: reorderedTickets });
    };

    const updateTicket = (id, updates) => {
        updateFormData({
            tickets: formData.tickets?.map(t =>
                t.id === id ? { ...t, ...updates } : t
            ),
        });
    };

    const deleteTicket = (id) => {
        updateFormData({
            tickets: formData.tickets?.filter(t => t.id !== id),
        });
    };

    return (
        <div className="space-y-8">
            {/* Page Header */}
            <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Ticket Configuration</h2>
                <p className="text-gray-600">
                    Set up your ticket types, pricing, and regional variations
                </p>
            </div>

            {/* Price by Date Toggle */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Dynamic Pricing by Date
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Enable different pricing for tickets based on registration date (Early bird, Regular, Last minute)
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => updateFormData({ enablePriceByDate: !formData.enablePriceByDate })}
                        className={`
              relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
              ${formData.enablePriceByDate ? 'bg-blue-600' : 'bg-gray-300'}
            `}
                    >
                        <span
                            className={`
                inline-block h-6 w-6 transform rounded-full bg-white transition-transform
                ${formData.enablePriceByDate ? 'translate-x-9' : 'translate-x-1'}
              `}
                        />
                    </button>
                </div>
                {formData.enablePriceByDate && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm text-blue-800">
                            ✓ Price by date enabled. You can add date-based pricing when creating tickets.
                        </p>
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                    <button
                        onClick={() => setActiveTab('regions')}
                        className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'regions'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
            `}
                    >
                        <Globe className="w-5 h-5 inline mr-2" />
                        Regions ({formData.ticketRegions?.length || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('categories')}
                        className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'categories'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
            `}
                    >
                        <Layers className="w-5 h-5 inline mr-2" />
                        Categories ({formData.ticketCategories?.length || 0})
                    </button>
                    <button
                        onClick={() => setActiveTab('tickets')}
                        className={`
              py-4 px-1 border-b-2 font-medium text-sm transition-colors
              ${activeTab === 'tickets'
                                ? 'border-blue-500 text-blue-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                            }
            `}
                    >
                        <Tag className="w-5 h-5 inline mr-2" />
                        Tickets ({formData.tickets?.length || 0})
                    </button>
                </nav>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
                {activeTab === 'regions' && (
                    <RegionsTab
                        regions={formData.ticketRegions || []}
                        addRegion={addRegion}
                        updateRegion={updateRegion}
                        deleteRegion={deleteRegion}
                    />
                )}

                {activeTab === 'categories' && (
                    <CategoriesTab
                        categories={formData.ticketCategories || []}
                        addCategory={addCategory}
                        updateCategory={updateCategory}
                        deleteCategory={deleteCategory}
                    />
                )}

                {activeTab === 'tickets' && (
                    <TicketsTab
                        tickets={formData.tickets || []}
                        regions={formData.ticketRegions || []}
                        categories={formData.ticketCategories || []}
                        enablePriceByDate={formData.enablePriceByDate || false}
                        addTicket={addTicket}
                        updateTicket={updateTicket}
                        deleteTicket={deleteTicket}
                        expandedTicketId={expandedTicketId}
                        setExpandedTicketId={setExpandedTicketId}
                        moveTicket={moveTicket}
                    />
                )}
            </div>
        </div>
    );
};

// Regions Tab Component
const RegionsTab = ({ regions, addRegion, updateRegion, deleteRegion }) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Ticket Regions</h3>
                    <p className="text-sm text-gray-600 mt-1">
                        Define geographical or demographic regions for ticket pricing (e.g., Indian, International)
                    </p>
                </div>
                <button
                    onClick={addRegion}
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Add Region
                </button>
            </div>

            {regions.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
                    <Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600 mb-4">No regions defined yet</p>
                    <button
                        onClick={addRegion}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        <Plus className="w-5 h-5 mr-2" />
                        Add Your First Region
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {regions.map((region) => (
                        <div key={region.id} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-all">
                            <div className="flex items-start justify-between mb-4">
                                <Globe className="w-8 h-8 text-blue-600" />
                                <button
                                    onClick={() => deleteRegion(region.id)}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Region Name
                                    </label>
                                    <input
                                        type="text"
                                        value={region.name}
                                        onChange={(e) => updateRegion(region.id, { name: e.target.value.toUpperCase() })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent uppercase font-semibold"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Currency
                                    </label>
                                    <select
                                        value={region.currency || 'INR'}
                                        onChange={(e) => updateRegion(region.id, { currency: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-medium"
                                    >
                                        <option value="INR">INR (₹)</option>
                                        <option value="USD">USD ($)</option>
                                        <option value="EUR">EUR (€)</option>
                                        <option value="GBP">GBP (£)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description (Optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={region.description || ''}
                                        onChange={(e) => updateRegion(region.id, { description: e.target.value })}
                                        placeholder="e.g., For Indian residents"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <div className="flex">
                    <Info className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                        <strong>Tip:</strong> Regions help you organize tickets by geography or demographics.
                        Each region can have its own currency for pricing.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TicketConfigStep;
export { RegionsTab };